import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { GpsDevice } from '@/types/gps';
import { toast } from 'sonner';

export function useGpsDeviceManagement(companyId?: string) {
  const queryClient = useQueryClient();

  // Fetch devices
  const { data: devices, isLoading } = useQuery({
    queryKey: ['gps-device-management', companyId],
    queryFn: async (): Promise<GpsDevice[]> => {
      if (!companyId) return [];

      const { data, error } = await supabase.rpc('get_gps_devices_with_vehicles', {
        p_company_id: companyId
      });

      if (error) throw error;
      return (data as any[])?.map(d => ({
        id: d.device_id,
        company_id: d.company_id,
        code: d.code,
        imei_or_uid: d.imei_or_uid,
        model: d.model,
        protocol: d.protocol,
        sim_iccid: d.sim_iccid,
        chip_operator: d.chip_operator,
        vehicle_id: d.vehicle_id,
        status: d.status,
        battery_level: d.battery_level,
        last_signal_at: d.last_signal_at,
        latitude: d.latitude,
        longitude: d.longitude,
        speed_kmh: d.speed_kmh,
        ignition: d.last_ignition,
        created_at: d.created_at,
        updated_at: d.updated_at,
        vehicle_plate: d.vehicle_plate,
        vehicle_make: d.vehicle_make,
        vehicle_model: d.vehicle_model,
      })) || [];
    },
    enabled: !!companyId,
    refetchInterval: 30000,
  });

  // Create device
  const createDevice = useMutation({
    mutationFn: async (device: Omit<GpsDevice, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase.functions.invoke('trackingone-device-create', {
        body: {
          imei_or_uid: device.imei_or_uid,
          sim_iccid: device.sim_iccid,
          vehicle_id: device.vehicle_id,
          model: device.model || 'generic',
          protocol: device.protocol || 'osmand',
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to create device');
      return data.device;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gps-device-management'] });
      queryClient.invalidateQueries({ queryKey: ['gps-devices'] });
      toast.success('Dispositivo GPS criado com sucesso');
    },
    onError: (error: any) => {
      console.error('Failed to create GPS device:', error);
      
      let errorMessage = 'Falha ao criar dispositivo GPS';
      let errorHint = '';
      
      if (error?.message) {
        try {
          const errorData = JSON.parse(error.message);
          errorMessage = errorData.error || errorMessage;
          errorHint = errorData.hint || '';
        } catch {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage, {
        description: errorHint,
        duration: 6000,
      });
    },
  });

  // Update device
  const updateDevice = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<GpsDevice> & { id: string }) => {
      const { data, error } = await supabase.functions.invoke('trackingone-device-update', {
        body: {
          device_id: id,
          imei_or_uid: updates.imei_or_uid,
          code: updates.code,
          model: updates.model,
          sim_iccid: updates.sim_iccid,
          chip_operator: updates.chip_operator,
          vehicle_id: updates.vehicle_id,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to update device');
      return data.device;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gps-device-management'] });
      queryClient.invalidateQueries({ queryKey: ['gps-devices'] });
      toast.success('Dispositivo GPS atualizado com sucesso');
    },
    onError: (error: any) => {
      console.error('Failed to update GPS device:', error);
      
      let errorMessage = 'Falha ao atualizar dispositivo GPS';
      let errorHint = '';
      
      if (error?.message) {
        try {
          const errorData = JSON.parse(error.message);
          errorMessage = errorData.error || errorMessage;
          errorHint = errorData.hint || '';
        } catch {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage, {
        description: errorHint,
        duration: 6000,
      });
    },
  });

  // Delete device
  const deleteDevice = useMutation({
    mutationFn: async (deviceId: string) => {
      const { data, error } = await supabase.functions.invoke('trackingone-device-delete', {
        body: { device_id: deviceId },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to delete device');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gps-device-management'] });
      queryClient.invalidateQueries({ queryKey: ['gps-devices'] });
      toast.success('Dispositivo GPS excluído com sucesso');
    },
    onError: (error: any) => {
      console.error('Failed to delete GPS device:', error);
      
      let errorMessage = 'Falha ao excluir dispositivo GPS';
      let errorHint = '';
      
      if (error?.message) {
        try {
          const errorData = JSON.parse(error.message);
          errorMessage = errorData.error || errorMessage;
          errorHint = errorData.hint || '';
        } catch {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage, {
        description: errorHint,
        duration: 6000,
      });
    },
  });

  return {
    devices,
    isLoading,
    createDevice,
    updateDevice,
    deleteDevice,
  };
}
